/* eslint-disable max-lines-per-function */
import { SafetySessionStorage } from '@mail-core/std/storage';
import { Button, ButtonGroup, Spacing, Text, Title } from '@vkontakte/vkui';
import React, { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { abRequiredSignUpWhenDownloadingTouchSelector } from 'reactApp/appHelpers/featuresHelpers';
import { publicFrequentSingUpWhenDownloadingAB } from 'reactApp/appHelpers/featuresHelpers/features/publicFrequentSingUpWhenDownloading';
import { abPublicShowAuthWhenDownloadingNewModalSelector } from 'reactApp/appHelpers/featuresHelpers/features/publicShowAuthWhenDownloadingNewModal';
import { chooseVariant } from 'reactApp/appHelpers/featuresHelpers/utils';
import { PublicBenefitsOfAuthorizationModal } from 'reactApp/components/PublicBenefitsOfAuthorizationModal/PublicBenefitsOfAuthorizationModal';
import { isFolder as checkIsFolder } from 'reactApp/modules/file/utils';
import { authPopup } from 'reactApp/modules/ph/ph.thunkActions';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { REQUIRED_SIGNUP_POPUP_SHOWED_C } from 'reactApp/modules/requiredAuthorization/constants';
import { requiredAuthorizationHelpers } from 'reactApp/modules/requiredAuthorization/helpers';
import { reDownloadController } from 'reactApp/modules/requiredAuthorization/reDownloadController';
import { getCurrentRouteId } from 'reactApp/modules/router/router.selectors';
import { getItemById } from 'reactApp/modules/storage/storage.selectors';
import type { RootState } from 'reactApp/store';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { ERequiredSignUpTouchAnalytics, requiredSignUpTouchAnalytics } from 'reactApp/utils/requiredSignUpTouchGa';

import styles from './RequiredSignUpDialog.css';

const link = chooseVariant(() => abRequiredSignUpWhenDownloadingTouchSelector(false), {
    control: 'https://trk.mail.ru/c/kj3vg2',
    variant1: 'https://trk.mail.ru/c/kuj3p7',
    /**
     * @expFlag public-frequent-sing-up-when-downloading-ab
     * @ticket CLOUDWEB-20158
     * @ifLose Удалить первое условие
     * @nextline
     */
    variant2: publicFrequentSingUpWhenDownloadingAB
        ? publicFrequentSingUpWhenDownloadingAB.href
        : /**
         * @expFlag public-show-auth-when-downloading-new-modal
         * @ticket CLOUDWEB-20007
         * @ifLose Удалить условие и первую ссылку
         * @nextline
         */
        abPublicShowAuthWhenDownloadingNewModalSelector()
        ? 'https://trk.mail.ru/c/pxs9g5'
        : 'https://trk.mail.ru/c/xk6g56',
});

const sessionStorage = new SafetySessionStorage({});

export const RequiredSignUpDialog: FC<IPropsWithPopup> = ({ onClose }) => {
    const ref = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const routeId = useSelector(getCurrentRouteId);
    const abVariant = useMemo(() => abRequiredSignUpWhenDownloadingTouchSelector(), []);
    /**
     * @expFlag public-show-auth-when-downloading-new-modal
     * @ticket CLOUDWEB-20007
     * @nextline
     */
    const newModalVariant = useMemo(() => abPublicShowAuthWhenDownloadingNewModalSelector(), []);
    const item = useSelector((state: RootState) => getItemById(state, routeId));
    const isFolder = checkIsFolder(item);

    useEffect(() => {
        requiredSignUpTouchAnalytics(ERequiredSignUpTouchAnalytics.SHOW_POPUP, { type_public: isFolder ? 'folder' : 'file' });

        /**
         * @expFlag public-frequent-sing-up-when-downloading-ab
         * @ticket CLOUDWEB-20158
         * @ifLose Удалить первое условие
         * @nextline
         */
        if (publicFrequentSingUpWhenDownloadingAB && publicFrequentSingUpWhenDownloadingAB.type === 'b') {
            sessionStorage.setItem(REQUIRED_SIGNUP_POPUP_SHOWED_C, 'true');
        } else {
            storeHelper.setValue(REQUIRED_SIGNUP_POPUP_SHOWED_C, true);
        }
    }, [isFolder]);

    const handleDownloadApp = useCallback(() => {
        requiredSignUpTouchAnalytics(ERequiredSignUpTouchAnalytics.DOWNLOAD_APP_CLICK, { type_public: isFolder ? 'folder' : 'file' });
    }, [isFolder]);

    const handleClose = useCallback(() => {
        if (abVariant === 'variant2') {
            requiredAuthorizationHelpers.setShowDownloadTip();
        }
        onClose();
    }, [onClose, abVariant]);

    const handleSignUp = useCallback(() => {
        const successPage = requiredAuthorizationHelpers.getSuccessPage();

        dispatch(authPopup({ successPage, requiredAuthWhenDownload: '1' }));
        reDownloadController.resetDownloadHappened();

        requiredSignUpTouchAnalytics(ERequiredSignUpTouchAnalytics.SIGNUP_CLICK, { type_public: isFolder ? 'folder' : 'file' });
    }, [dispatch, isFolder]);

    const title = useMemo(
        () =>
            chooseVariant(abRequiredSignUpWhenDownloadingTouchSelector, {
                control: (
                    <>
                        Установите Облако
                        <br />
                        или войдите в аккаунт
                    </>
                ),
                variant1: (
                    <>
                        Установите Облако или
                        <br />
                        войдите, чтобы скачать {isFolder ? 'папку' : 'файл'}
                    </>
                ),
                variant2: (
                    <>
                        Установите Облако
                        <br />
                        или войдите в аккаунт
                    </>
                ),
            }),
        [isFolder]
    );

    const subtitle = useMemo(
        () =>
            chooseVariant(abRequiredSignUpWhenDownloadingTouchSelector, {
                control: (
                    <>
                        В Облаке можно удобно
                        <br />
                        просматривать и хранить файлы
                    </>
                ),
                variant1: (
                    <>
                        В Облаке можно удобно
                        <br />
                        просматривать и хранить файлы
                    </>
                ),
                variant2: (
                    <>
                        В Облаке удобно скачивать,
                        <br />
                        просматривать и хранить файлы
                    </>
                ),
            }),
        []
    );

    /**
     * @expFlag public-show-auth-when-downloading-new-modal
     * @ticket CLOUDWEB-20007
     * @start
     */
    if (newModalVariant === 'variant2') {
        const modalConfig = {
            theme: 'no-color' as const,
            title: 'Войдите в аккаунт Mail',
            subtitle: 'Сможете сохранить файлы, которыми с вами поделились — и полноценно работать с ними',
            buttons: [
                {
                    onClick: handleSignUp,
                    appearance: 'accent' as const,
                    text: 'Войти или создать аккаунт',
                },
                {
                    onClick: handleDownloadApp,
                    appearance: 'neutral' as const,
                    text: 'Скачать приложение и войти',
                    href: `https://trk.mail.ru/c/pxs9g5?mt_deeplink=${window.location.href}`,
                },
            ],
        };

        return (
            <PublicBenefitsOfAuthorizationModal
                theme={modalConfig.theme}
                title={modalConfig.title}
                subtitle={modalConfig.subtitle}
                buttons={modalConfig.buttons}
                onShow={() => {}}
                onClose={handleClose}
                onXClose={handleClose}
                onTapOutsideClose={handleClose}
            />
        );
    }
    /**
     * @expFlag public-show-auth-when-downloading-new-modal
     * @ticket CLOUDWEB-20007
     * @end
     */

    return (
        <MobileDialog
            id="required-sign-up-dialog"
            onClose={handleClose}
            mod="mobileV4"
            closeMode="round"
            topmost
            closeOnDimmerClick
            scrollRef={ref}
        >
            <div className={styles.root} ref={ref}>
                <Title className={styles.title}>{title}</Title>
                <Spacing size={8} />
                <Text className={styles.subtitle}>{subtitle}</Text>
                <Spacing size={24} />
                {/**
                 * @expFlag public-show-auth-when-downloading-new-modal
                 * @ticket CLOUDWEB-20007
                 * @ifLose Удалить условие на gap
                 * @nextline
                 */}
                <ButtonGroup mode="vertical" gap={newModalVariant === 'variant1' ? 's' : undefined}>
                    {/**
                     * @expFlag public-show-auth-when-downloading-new-modal
                     * @ticket CLOUDWEB-20007
                     * @start
                     */}
                    {newModalVariant === 'variant1' ? (
                        <>
                            <Button className={styles.btn} size="l" stretched appearance="accent" onClick={handleSignUp}>
                                <Text className={styles.btnText}>Войти или создать аккаунт</Text>
                            </Button>
                            <Button
                                Component="a"
                                href={`${link}?mt_deeplink=${window.location.href}`}
                                onClick={handleDownloadApp}
                                className={styles.btn}
                                size="l"
                                stretched
                                appearance="neutral"
                                activeMode={styles.active}
                                hasActive={false}
                            >
                                <Text className={styles.btnText}>Скачать приложение и войти</Text>
                            </Button>
                        </>
                    ) : (
                        /**
                         * @expFlag public-show-auth-when-downloading-new-modal
                         * @ticket CLOUDWEB-20007
                         * @end
                         */
                        <>
                            <Button
                                Component="a"
                                href={`${link}?mt_deeplink=${window.location.href}`}
                                onClick={handleDownloadApp}
                                className={styles.btn}
                                size="l"
                                stretched
                                appearance="accent"
                                before={<span className={styles.icon} />}
                                activeMode={styles.active}
                                hasActive={false}
                            >
                                <Text className={styles.btnText}>Установить приложение</Text>
                            </Button>
                            <Button className={styles.btn} size="l" stretched appearance="neutral" onClick={handleSignUp}>
                                <Text className={styles.btnText}>Войти в аккаунт</Text>
                            </Button>
                        </>
                    )}
                </ButtonGroup>
            </div>
        </MobileDialog>
    );
};
